import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollText, AlertTriangle } from "lucide-react";

// Legal waiver text
export const LEGAL_WAIVER_TEXT = {
  title: "TERMS AND CONDITIONS - LIABILITY WAIVER",
  sections: [
    {
      title: "1. RESPONSIBILITY FOR DAMAGES",
      content: `The Renter acknowledges and agrees that they are fully responsible for any and all damages to the rental vehicle during the rental period, regardless of fault, including but not limited to: collision damage, vandalism, theft, weather-related damage, interior damage, mechanical damage resulting from misuse, and damage to tires, wheels, windshield, and undercarriage. The Renter agrees to pay for all repair costs or the fair market value of the vehicle if it is deemed a total loss.`
    },
    {
      title: "2. TRAFFIC VIOLATIONS AND TICKETS",
      content: `The Renter is solely responsible for any and all traffic violations, parking tickets, toll violations, photo enforcement tickets, and any other citations or fines incurred during the rental period. The Renter authorizes Ak Car Rental to charge their credit card on file for any such violations plus an administrative fee of $50.00 per violation. The Renter agrees to pay all fines promptly and indemnify Ak Car Rental against any claims arising from traffic violations.`
    },
    {
      title: "3. NO SMOKING POLICY",
      content: `Smoking, vaping, or the use of any tobacco or cannabis products inside the rental vehicle is strictly prohibited. If evidence of smoking is detected upon vehicle return (including odor, ash, burn marks, or residue), a cleaning and detailing fee of $500.00 will be charged to the Renter's credit card on file. This fee covers the cost of professional interior detailing, ozone treatment, and any necessary repairs to restore the vehicle to a smoke-free condition.`
    },
    {
      title: "4. FUEL POLICY",
      content: `The vehicle must be returned with the same fuel level as at the time of pickup (typically full tank). If the vehicle is returned with less fuel than at pickup, the Renter will be charged $35.00 for each quarter tank (1/4) of fuel that is missing. For example: returning with 3/4 tank = $35.00 charge; returning with 1/2 tank = $70.00 charge; returning with 1/4 tank = $105.00 charge; returning empty = $140.00 charge. These charges will be applied to the credit card on file.`
    },
    {
      title: "5. VEHICLE CLEANLINESS",
      content: `The vehicle must be returned in reasonably clean condition, similar to its condition at pickup. If the vehicle is returned excessively dirty (including but not limited to: excessive mud, sand, pet hair, food debris, stains, spills, or garbage), a detailing fee of $150.00 will be charged to the Renter's credit card on file. Normal wear and light dust from regular use is acceptable and will not incur additional charges.`
    },
    {
      title: "6. INSURANCE AND LIABILITY",
      content: `The Renter acknowledges that the basic rental rate does not include comprehensive insurance coverage. Any insurance coverage selected is subject to deductibles and limitations as specified. The Renter's personal auto insurance may or may not provide coverage for rental vehicles. The Renter is responsible for understanding their own insurance coverage and agrees to bear any costs not covered by insurance.`
    },
    {
      title: "7. PROHIBITED USES",
      content: `The vehicle shall not be used for: illegal purposes, towing, racing, off-road driving, transporting hazardous materials, transporting pets without prior approval and pet fee payment, or subletting the vehicle to any other party. Any unauthorized driver operating the vehicle voids all coverage and the Renter assumes full liability. Violation of these terms may result in immediate termination of the rental agreement and the Renter will be liable for all associated costs.`
    },
    {
      title: "8. RETURN CONDITIONS",
      content: `The vehicle must be returned on the agreed date and time to the specified return location with all accessories, keys, and documents provided at pickup. Late returns will incur additional daily charges at the standard daily rate plus a $25.00 late fee. Failure to return the vehicle within 24 hours of the scheduled return time without contacting Ak Car Rental may be reported as theft to law enforcement authorities.`
    },
    {
      title: "9. INDEMNIFICATION",
      content: `The Renter agrees to indemnify, defend, and hold harmless Ak Car Rental, its owners, employees, and agents from and against any and all claims, damages, losses, costs, and expenses (including attorney fees) arising from the Renter's use of the vehicle or breach of this agreement.`
    }
  ],
  feesSummary: [
    { item: "Smoking/Vaping in Vehicle", fee: "$500.00" },
    { item: "Missing Fuel (per quarter tank)", fee: "$35.00" },
    { item: "Excessive Dirt/Cleaning Required", fee: "$150.00" },
    { item: "Traffic Violation Admin Fee", fee: "$50.00/violation" },
    { item: "Late Return Fee", fee: "$25.00 + daily rate" }
  ],
  acknowledgment: `By signing this agreement, I acknowledge that I have read, understood, and agree to be bound by all the terms and conditions stated above. I understand that I am financially responsible for any damages to the vehicle, all traffic violations, and any applicable fees for smoking, fuel, or cleaning as outlined above.`
};

/**
 * Legal Waiver Section Component for Rental Agreement
 */
export const LegalWaiverSection = ({ waiverAccepted, setWaiverAccepted, waiverInitials, setWaiverInitials, isEditing }) => {
  return (
    <Card className="border-warning/30 bg-warning/5" data-testid="legal-waiver-section">
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-warning" />
          {LEGAL_WAIVER_TEXT.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-3 mb-4 rounded-lg bg-warning/10 border border-warning/30">
          <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <p className="text-sm text-warning-foreground">
            <strong>Important:</strong> Please read the following terms and conditions carefully before signing.
          </p>
        </div>
        
        {/* Legal Terms */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {LEGAL_WAIVER_TEXT.sections.map((section, index) => (
            <div key={index} className="text-sm">
              <h4 className="font-semibold text-foreground mb-1">{section.title}</h4>
              <p className="text-muted-foreground leading-relaxed">{section.content}</p>
            </div>
          ))}
          
          {/* Fees Summary Table */}
          <div className="mt-6 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
            <h4 className="font-semibold text-destructive mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              FEES SUMMARY
            </h4>
            <div className="space-y-2">
              {LEGAL_WAIVER_TEXT.feesSummary.map((fee, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{fee.item}</span>
                  <span className="font-semibold text-destructive">{fee.fee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Acknowledgment */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-sm font-medium text-foreground mb-4">
            {LEGAL_WAIVER_TEXT.acknowledgment}
          </p>
          
          {/* Acceptance Checkbox */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-card border">
            {isEditing ? (
              <Checkbox
                id="waiver-accepted"
                checked={waiverAccepted}
                onCheckedChange={setWaiverAccepted}
                className="mt-0.5"
                data-testid="waiver-checkbox"
              />
            ) : (
              <div className={`w-5 h-5 rounded flex items-center justify-center ${
                waiverAccepted ? 'bg-success/20' : 'bg-destructive/20'
              }`}>
                {waiverAccepted ? (
                  <ScrollText className="w-3 h-3 text-success" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-destructive" />
                )}
              </div>
            )}
            <label htmlFor="waiver-accepted" className="text-sm cursor-pointer flex-1">
              <span className="font-medium">
                I have read and agree to all terms and conditions
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                By checking this box, you acknowledge your responsibility for damages and traffic violations.
              </p>
            </label>
          </div>
          
          {/* Initials Box */}
          <div className="mt-4 p-4 rounded-lg bg-card border">
            <Label htmlFor="waiver-initials" className="text-sm font-medium flex items-center gap-2 mb-2">
              <ScrollText className="w-4 h-4 text-primary" />
              Customer Initials
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Please enter your initials to acknowledge you have read and understood all terms, conditions, and fees listed above.
            </p>
            {isEditing ? (
              <div className="flex items-center gap-3">
                <Input
                  id="waiver-initials"
                  value={waiverInitials || ''}
                  onChange={(e) => setWaiverInitials(e.target.value.toUpperCase())}
                  placeholder="e.g., JD"
                  maxLength={5}
                  className="w-24 text-center text-lg font-bold uppercase tracking-widest"
                  data-testid="waiver-initials-input"
                />
                <span className="text-xs text-muted-foreground">
                  (Enter your initials)
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className={`px-4 py-2 rounded border-2 border-dashed min-w-[80px] text-center ${
                  waiverInitials ? 'border-success bg-success/5' : 'border-muted-foreground/30'
                }`}>
                  <span className={`text-lg font-bold uppercase tracking-widest ${
                    waiverInitials ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {waiverInitials || '—'}
                  </span>
                </div>
                {!waiverInitials && (
                  <span className="text-xs text-muted-foreground">(No initials provided)</span>
                )}
              </div>
            )}
          </div>
          
          {!waiverAccepted && !isEditing && (
            <p className="text-xs text-destructive mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Waiver has not been accepted
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LegalWaiverSection;
